'use client';

import { useEffect } from 'react';
import { getStoredUserId, getUserData } from '@/helpers/local-storage';
import { setCsp13nCookie, removeCsp13nCookie, sendUserInfoToLytics } from '@/lib/p13n';
import { USER_DATA_UPDATED_EVENT, USER_LOGGED_OUT_EVENT } from '@/lib/constants/auth-events';

/**
 * Keeps the `_cs_p13n` cookie and Lytics profile in sync with the stored user profile.
 * Personalization only observes the profile — auth code never pushes to it directly.
 * Reconciles on mount (the common case: sign-in ends in a full reload) plus the
 * profile-changed/cleared events and cross-tab storage events. Renders nothing.
 */
export function PersonalizationIdentity() {
  useEffect(() => {
    // Syncs a stored profile, or clears the cookie if there isn't one — reading the store
    // instead of the event payload means logout needs no special-casing.
    const reconcile = () => {
      const user = getUserData();
      const uid = getStoredUserId(user);
      const email = (user?.email ?? user?.emailAddress) as string | undefined;

      // No identity to target with — nothing partial gets written.
      if (!uid || !email) {
        removeCsp13nCookie();
        return;
      }

      const role = (user?.role as string | undefined) ?? '';
      setCsp13nCookie(uid, role);
      sendUserInfoToLytics(
        uid,
        role,
        (user?.firstName as string | undefined) ?? '',
        (user?.lastName as string | undefined) ?? '',
        email
      );
    };

    const onStorage = (event: StorageEvent) => {
      if (event.key === 'user') reconcile();
    };

    reconcile();
    window.addEventListener(USER_DATA_UPDATED_EVENT, reconcile);
    window.addEventListener(USER_LOGGED_OUT_EVENT, reconcile);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(USER_DATA_UPDATED_EVENT, reconcile);
      window.removeEventListener(USER_LOGGED_OUT_EVENT, reconcile);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  return null;
}
